import { type FieldValue, SECRET_NAMES, type SecretsPatch } from '@/domain/providerConfig';

import type { FormDiff } from '../form/formDiff';

/** Typed secrets trimmed as the server's normalizeSecret does; a clear stays null. */
function secretScope(patch: SecretsPatch): Record<string, string | null> {
    const out: Record<string, string | null> = {};
    for (const name of SECRET_NAMES) {
        const value = patch[name];
        if (typeof value === 'string') out[name] = value.trim();
        else if (value === null) out[name] = null;
    }
    return out;
}

function sortedRecord(record: Record<string, FieldValue>): [string, FieldValue][] {
    return Object.keys(record)
        .sort()
        .map((key): [string, FieldValue] => [key, record[key]]);
}

/**
 * What a test token vouches for, as the server's testDigest reads a save: the site
 * connection and secrets the save changes, and each pen's own secrets and connection
 * overrides. Two diffs with the same scope can share a token; any other edit cannot
 * change whether it still holds. Kept in memory only, since it holds typed secrets.
 */
export function testScopeKey(diff: FormDiff): string {
    const pens = Object.entries(diff.pens)
        .map(([cageId, pen]) => ({
            cageId: Number(cageId),
            secrets: secretScope(pen.secrets),
            connection: sortedRecord(pen.connectionChanges),
        }))
        .filter((pen) => Object.keys(pen.secrets).length > 0 || pen.connection.length > 0)
        .sort((a, b) => a.cageId - b.cageId);
    return JSON.stringify({
        connection: sortedRecord(diff.connection),
        secrets: secretScope(diff.secrets),
        pens,
    });
}
